import { useTranslation } from 'react-i18next';

import ArrowRightLine from '@/assets/icons/arrow_right_line.svg?component';
import { AmountRatioWithText } from '@/components/atoms/AmountRatioWithText';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import AverageHeader from '@/components/molecules/AverageHeader';
import ContentBlock from '@/components/molecules/ContentBlock';
import { type SortBy, SortByDropdown } from '@/components/molecules/SortByDropdown';
import {
    LocationOverviewTable,
    useLocationOverviewRows,
} from '@/components/organisms/LocationOverviewTable';
import { type OverviewLocation, speciesLabelKey, summariseOverviewSpecies } from '@/domain/client';
import type { LocationsLicenseOverview } from '@/domain/licenses';
import type { MarkHistoricalInput } from '@/domain/locations';
import { isLandbasedLocation, isSmoltLocation } from '@/utils/locationTypeHelpers';

import { amountOfActiveBioscopes, totalAmountOfActiveBioscopes } from '../regionOverviewMetrics';

import styles from '../RegionOverview.module.scss';

export interface LocationOverviewBoxProps {
    location: OverviewLocation;
    locationLicense: LocationsLicenseOverview | undefined;
    clientId: number;
    hasAccessToMarkHistorical: boolean;
    markHistorical: (changes: MarkHistoricalInput[]) => Promise<unknown>;
    isOptoScaleAdmin: boolean;
    renderLazy: boolean;
    isActive: boolean;
    sortBy: SortBy;
    onSortChange: (next: SortBy) => void;
    locationUrl: string;
}

export default function LocationOverviewBox(props: LocationOverviewBoxProps) {
    const {
        location,
        locationLicense,
        clientId,
        hasAccessToMarkHistorical,
        markHistorical,
        isOptoScaleAdmin,
        renderLazy,
        isActive,
        sortBy,
        onSortChange,
        locationUrl,
    } = props;
    const { t } = useTranslation();

    const rows = useLocationOverviewRows(location, sortBy);

    // What this location can honestly say about species: its pens each carry their own, so the
    // site default is only a fallback for a location that has no pens yet (WB-2207).
    const species = summariseOverviewSpecies(location.cages);
    const labelKey = speciesLabelKey(species, location.locationFishType);
    const speciesLabel = labelKey ? t(labelKey.key, { defaultValue: labelKey.defaultValue }) : '-';

    return (
        <ContentBlock>
            {renderLazy && (
                <>
                    {/* The badge colour keys off a single species; a mixed site gets the
                        default header colour rather than claiming one of them. */}
                    <div
                        className={`${styles.header} `}
                        data-species={species.single?.shortName ?? undefined}>
                        <div className={styles.leftSideHeaderContainer}>
                            <h2>{location.locationName}</h2>

                            <div className={styles.fishNameContainer}>{speciesLabel}</div>
                        </div>
                        <div className={styles.tableHeaderRow}>
                            <SortByDropdown
                                value={sortBy}
                                onChange={onSortChange}
                                disabled={!isActive}
                            />
                            <Button
                                size={'medium'}
                                variant="primary"
                                iconEnd={<SvgIcon icon={ArrowRightLine} color="currentColor" />}
                                trackingId="region_overview_go_to_location"
                                to={locationUrl}>
                                {isSmoltLocation(location?.locationType) ||
                                isLandbasedLocation(location?.locationType)
                                    ? t('regionOverview.regionOverview.goToFacility')
                                    : t('regionOverview.regionOverview.goToSite')}
                            </Button>
                        </div>
                    </div>
                    <div className={styles.activeinactivebioscopes}>
                        <AmountRatioWithText
                            leftSideAmount={amountOfActiveBioscopes([location])}
                            rightSideAmount={totalAmountOfActiveBioscopes([location])}
                            text={'regionOverview.common.bioscopesActivelyMeasuring'}
                        />
                    </div>
                    {!isSmoltLocation(location?.locationType) && (
                        <>
                            <AverageHeader
                                location={location}
                                locationLicense={locationLicense as LocationsLicenseOverview}
                                isOptoScaleAdmin={isOptoScaleAdmin}
                            />
                            <hr />
                        </>
                    )}
                    <LocationOverviewTable
                        key={location.locationId}
                        rows={rows}
                        locationId={location.locationId}
                        locationType={location.locationType}
                        locationLicense={locationLicense}
                        isOptoScaleAdmin={isOptoScaleAdmin}
                        hasAccessToMarkHistorical={hasAccessToMarkHistorical}
                        markHistorical={markHistorical}
                        hideMarkHistorical={true}
                        clientId={clientId}
                        hideNoMeasurements={true}
                        hideInactive={true}
                    />
                </>
            )}
        </ContentBlock>
    );
}
